// src/NewAlertsPage.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  DatePicker as AntDatePicker,
  TimePicker as AntTimePicker,
  Select as AntSelect,
  Pagination as AntPagination,
  Spin,
  Empty,
  message,
} from "antd";
import dayjs from "dayjs";
import {
  ExclamationTriangleIcon,
  CameraIcon,
  PhotoIcon,
  MapPinIcon,
  ClipboardDocumentListIcon,
  ChevronDownIcon,
  XMarkIcon,
  CalendarDaysIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { exportAlertsList, getAlertsList } from "../../services/AlertsTable";
import ImageModalPopup from "./ImageModalPopup";
import { toast } from "react-toastify";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { DropDownValues } from "../../services/Filters";
import AlertStatusModal from "./Component/AlertStatusModal";
import jwt_decode from "jwt-decode";
import { decryptRequest } from "../../utils/crypt";
import FullScreenLoader from "./Component/FullScreenLoader";

const AntdSelectField = ({ value, options, placeholder, onChange, mode }) => (
  <AntSelect
    mode={mode}
    style={{ width: "100%" }}
    placeholder={placeholder}
    value={value || (mode === "multiple" ? [] : undefined)}
    onChange={onChange}
    options={options}
    allowClear
    showSearch={mode === "multiple"}
    optionFilterProp={mode === "multiple" ? "label" : undefined}
    className="custom-ant-select"
    getPopupContainer={(triggerNode) => triggerNode.parentNode}
  />
);

const FiltersDropdownContent = ({
  onClose,
  onApplyFilters,
  initialFilters,
  locationOptions,
  cameraOptions,
  alertOptions,
  assignedToOptions,
}) => {
  const [startDate, setStartDate] = useState(initialFilters.startDate);
  const [endDate, setEndDate] = useState(initialFilters.endDate);
  const [startTime, setStartTime] = useState(initialFilters.startTime);
  const [endTime, setEndTime] = useState(initialFilters.endTime);

  const [selectedLocation, setSelectedLocation] = useState(
    initialFilters.location || []
  );
  const [selectedCamera, setSelectedCamera] = useState(
    initialFilters.camera || []
  );
  const [selectedAlerts, setSelectedAlerts] = useState(
    initialFilters.alert || []
  );
  const [selectedStatus, setSelectedStatus] = useState(
    initialFilters.status || []
  );
  const [selectedAssignedTo, setSelectedAssignedTo] = useState(
    initialFilters.assignedTo || []
  );

  // Note: Data fetching logic has been moved to NewAlertsPage

  const statusOptions = [
    { value: "Open", label: "Open" },
    { value: "Close", label: "Close" },
    { value: "In Progress", label: "In Progress" },
    { value: "Assigned", label: "Assigned" },
  ];

  const handleGetData = () => {
    onApplyFilters({
      startDate,
      endDate,
      startTime,
      endTime,
      location: selectedLocation,
      camera: selectedCamera,
      alert: selectedAlerts,
      status: selectedStatus,
      assignedTo: selectedAssignedTo,
    });
    onClose();
  };

  return (
    <div className="absolute top-full right-0 mt-2 w-80 md:w-96 bg-white border border-gray-200 rounded-lg shadow-xl p-4 z-50">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-700">Filters</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <AntDatePicker
            value={startDate}
            onChange={setStartDate}
            format="YYYY-MM-DD"
            style={{ width: "100%" }}
            placeholder="Start Date"
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          />
          <AntDatePicker
            value={endDate}
            onChange={setEndDate}
            format="YYYY-MM-DD"
            disabledDate={(current) =>
              startDate && current && current.isBefore(startDate, "day")
            }
            style={{ width: "100%" }}
            placeholder="End Date"
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <AntTimePicker
            value={startTime}
            onChange={setStartTime}
            format="HH:mm:ss"
            style={{ width: "100%" }}
            placeholder="Start Time"
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          />
          <AntTimePicker
            value={endTime}
            onChange={setEndTime}
            format="HH:mm:ss"
            style={{ width: "100%" }}
            placeholder="End Time"
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          />
        </div>
        <AntdSelectField
          value={selectedLocation}
          options={locationOptions}
          placeholder="Select Locations"
          onChange={setSelectedLocation}
          mode="multiple"
        />
        <AntdSelectField
          value={selectedCamera}
          options={cameraOptions}
          placeholder="Select Cameras"
          onChange={setSelectedCamera}
          mode="multiple"
        />
        <AntdSelectField
          value={selectedAlerts}
          options={alertOptions}
          placeholder="Select Alerts"
          onChange={setSelectedAlerts}
          mode="multiple"
        />
        <AntdSelectField
          value={selectedStatus}
          options={statusOptions}
          placeholder="Select Status"
          onChange={setSelectedStatus}
          mode="multiple"
        />
        <AntdSelectField
          value={selectedAssignedTo}
          options={assignedToOptions}
          placeholder="Select assigned to"
          onChange={setSelectedAssignedTo}
          mode="multiple"
        />
        <button
          className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-150 mt-1"
          onClick={handleGetData}
        >
          Get Data
        </button>
      </div>
    </div>
  );
};

const StatusTag = ({ type, text, onClick }) => {
  let bgColor,
    textColor,
    borderColor,
    cursorStyle,
    hoverBgColor,
    sizeAndFontStyles;
  const upperText = text?.toUpperCase() || "N/A";
  if (type === "location") {
    sizeAndFontStyles = "px-3 py-1 text-xs font-medium border";
    bgColor = upperText.includes("WAREHOUSE")
      ? "bg-green-100"
      : "bg-orange-100";
    textColor = upperText.includes("WAREHOUSE")
      ? "text-green-700"
      : "text-orange-700";
    borderColor = upperText.includes("WAREHOUSE")
      ? "border-green-300"
      : "border-orange-300";
  } else if (type === "status") {
    sizeAndFontStyles = "px-4 py-2 text-sm font-semibold shadow-sm";
    switch (upperText) {
      case "OPEN":
        bgColor = "bg-red-500";
        textColor = "text-white";
        hoverBgColor = "hover:bg-red-600";
        break;
      case "INPROGRESS":
        bgColor = "bg-yellow-400";
        textColor = "text-yellow-900";
        hoverBgColor = "hover:bg-yellow-500";
        break;
      case "ASSIGNED":
        bgColor = "bg-blue-500";
        textColor = "text-white";
        hoverBgColor = "hover:bg-blue-600";
        break;
      case "CLOSE":
      case "CLOSED":
        bgColor = "bg-green-500";
        textColor = "text-white";
        hoverBgColor = "hover:bg-green-600";
        break;
      default:
        bgColor = "bg-gray-500";
        textColor = "text-white";
        hoverBgColor = "hover:bg-gray-600";
    }

    cursorStyle = onClick ? "cursor-pointer" : "";
    borderColor = "border-transparent";
  }
  return (
    <span
      className={`
        inline-block text-center rounded-md transition-all
        ${sizeAndFontStyles}
        ${bgColor || ""}
        ${textColor || ""}
        ${borderColor || ""}
        ${cursorStyle || ""}
        ${onClick && hoverBgColor ? hoverBgColor : ""}
      `}
      onClick={onClick}
    >
      {upperText}
    </span>
  );
};

function NewAlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [spinning, setSpinning] = React.useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  const initialFilterValues = {
    startDate: dayjs().subtract(30, "day"),
    endDate: dayjs(),
    startTime: dayjs("00:00:00", "HH:mm:ss"),
    endTime: dayjs(),
    location: [],
    camera: [],
    alert: [],
    status: [],
    assignedTo: [],
  };
  const [currentFilters, setCurrentFilters] = useState(initialFilterValues);

  const [selectedAlertIds, setSelectedAlertIds] = useState(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const filtersButtonRef = useRef(null);
  const filtersDropdownRef = useRef(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImageForModal, setSelectedImageForModal] = useState("");
  const [selectedImageNameForModal, setSelectedImageNameForModal] =
    useState("alert-image.jpg");

  // --- NEW MODAL STATE ---
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedAlertForModal, setSelectedAlertForModal] = useState(null);

  // --- LIFTED STATE FOR DROPDOWN OPTIONS ---
  const [locationOptions, setLocationOptions] = useState([]);
  const [cameraOptions, setCameraOptions] = useState([]);
  const [alertOptions, setAlertOptions] = useState([]);
  const [assignedToOptions, setAssignedToOptions] = useState([]);

  // --- FETCH DROPDOWN DATA ---
  const getDropDownData = async () => {
    try {
      let dropDownValues = await DropDownValues();
      let { alerts, cameras, locations, emails } = dropDownValues;
      setLocationOptions(locations);
      setCameraOptions(cameras);
      setAlertOptions(alerts);
      setAssignedToOptions(emails);
    } catch (error) {
      console.error("Failed to fetch dropdown values:", error);
      toast.error("Could not load filter options.");
    }
  };

  useEffect(() => {
    getDropDownData();
  }, []);

  const fetchAlertsList = useCallback(async (filters, pageInfo) => {
    setLoading(true);
    setSelectedAlertIds(new Set());
    setIsAllSelected(false);

    const params = {
      startDate: filters.startDate.format("YYYY-MM-DD"),
      endDate: filters.endDate.format("YYYY-MM-DD"),
      startTime: filters.startTime.format("HH:mm:ss"),
      endTime: filters.endTime.format("HH:mm:ss"),
      page: pageInfo.currentPage,
      limit: pageInfo.limit,
      ...(filters.location &&
        filters.location.length > 0 && { location: filters.location }),
      ...(filters.camera &&
        filters.camera.length > 0 && { camera: filters.camera }),
      ...(filters.alert &&
        filters.alert.length > 0 && { alert: filters.alert }),
      ...(filters.status &&
        filters.status.length > 0 && { status: filters.status }),
      ...(filters.assignedTo &&
        filters.assignedTo.length > 0 && {
          alertsAssignedMails: filters.assignedTo,
        }),
    };

    try {
      const response = await getAlertsList(params);
      if (response && response.data && response.data.data) {
        setDataSource(response.data.data.data);
        setAlerts(response.data.data.data || []);
        setPagination(
          response.data.data.pagination || {
            currentPage: 1,
            limit: 10,
            total: 0,
            totalPages: 1,
          }
        );
      } else {
        message.error("Failed to fetch alerts or received invalid data.");
        setAlerts([]);
        setPagination({ currentPage: 1, limit: 10, total: 0, totalPages: 1 });
      }
    } catch (error) {
      console.error("Error fetching alerts list:", error);
      message.error(
        `Error fetching alerts: ${error.message || "Unknown error"}`
      );
      setAlerts([]);
      setPagination({ currentPage: 1, limit: 10, total: 0, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlertsList(currentFilters, pagination);
  }, [fetchAlertsList]);

  const handleApplyFilters = (newFilters) => {
    const updatedFilters = { ...currentFilters, ...newFilters };
    setCurrentFilters(updatedFilters);
    const newPagination = { ...pagination, currentPage: 1 };
    setPagination(newPagination);
    fetchAlertsList(updatedFilters, newPagination);
  };

  const handlePaginationChange = (page, pageSize) => {
    const newPagination = { ...pagination, currentPage: page, limit: pageSize };
    setPagination(newPagination);
    fetchAlertsList(currentFilters, newPagination);
  };

  const handleOpenStatusModal = (alert) => {
    setSelectedAlertForModal(alert);
    setIsStatusModalOpen(true);
  };

  const handleCloseStatusModal = () => {
    setIsStatusModalOpen(false);
    setSelectedAlertForModal(null);
  };

  const handleUpdateAlertStatus = () => {
    fetchAlertsList(currentFilters, pagination);
  };

  useEffect(() => {
    if (
      !loading &&
      alerts.length > 0 &&
      selectedAlertIds.size === alerts.length
    ) {
      setIsAllSelected(true);
    } else {
      setIsAllSelected(false);
    }
  }, [selectedAlertIds, alerts, loading]);

  // useEffect(() => {
  //   function handleClickOutside(event) {
  //     if (
  //       showFilters &&
  //       filtersButtonRef.current &&
  //       !filtersButtonRef.current.contains(event.target) &&
  //       filtersDropdownRef.current &&
  //       !filtersDropdownRef.current.contains(event.target)
  //     ) {
  //       setShowFilters(false);
  //     }
  //   }
  //   document.addEventListener("mousedown", handleClickOutside);
  //   return () => document.removeEventListener("mousedown", handleClickOutside);
  // }, [showFilters]);

  const handleSelectAllChange = (event) => {
    const { checked } = event.target;
    setIsAllSelected(checked);
    if (checked) {
      setSelectedAlertIds(new Set(alerts.map((alert) => alert._id)));
    } else {
      setSelectedAlertIds(new Set());
    }
  };
  // const handleRowSelectChange = (alertId, event) => {
  //   const { checked } = event.target;
  //   const newSelectedAlertIds = new Set(selectedAlertIds);
  //   if (checked) newSelectedAlertIds.add(alertId);
  //   else newSelectedAlertIds.delete(alertId);
  //   setSelectedAlertIds(newSelectedAlertIds);
  // };

  const handleImageClickForPopup = (imageUrl, alertDetails) => {
    setSelectedImageForModal(imageUrl);
    const name =
      `${alertDetails.camera}_${alertDetails.alert}_${alertDetails.date}_${alertDetails.time}.jpg`.replace(
        /[^a-z0-9_.-]/gi,
        "_"
      );
    setSelectedImageNameForModal(name);
    setIsImageModalOpen(true);
  };
  const handleCloseImageModalForPopup = () => {
    setIsImageModalOpen(false);
    setSelectedImageForModal("");
    setSelectedImageNameForModal("alert-image.jpg");
  };

  const ExportToExcel = ({ fileName }) => {
    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

    const fileExtension = ".xlsx";

    const exportToCSV = async (fileName) => {
      setSpinning(true);
      const params = {
        startDate: currentFilters.startDate.format("YYYY-MM-DD"),
        endDate: currentFilters.endDate.format("YYYY-MM-DD"),
        startTime: currentFilters.startTime.format("HH:mm:ss"),
        endTime: currentFilters.endTime.format("HH:mm:ss"),
        ...(currentFilters.location &&
          currentFilters.location.length > 0 && {
            location: currentFilters.location,
          }),
        ...(currentFilters.camera &&
          currentFilters.camera.length > 0 && {
            camera: currentFilters.camera,
          }),
        ...(currentFilters.alert &&
          currentFilters.alert.length > 0 && { alert: currentFilters.alert }),
        ...(currentFilters.status &&
          currentFilters.status.length > 0 && {
            status: currentFilters.status,
          }),
        ...(currentFilters.assignedTo &&
          currentFilters.assignedTo.length > 0 && {
            alertsAssignedMails: currentFilters.assignedTo,
          }),
      };
      let apiData = await exportAlertsList(params);
      if (apiData?.data?.length > 0) {
        toast.success("Export is in progress.Please wait.");
        let userSheetData = apiData?.data?.map((item) => {
          return {
            Date: item.date,
            time: item.time,
            alert: item.alert,
            camera: item.camera,
            status: item.status ? item.status : "",
            image: {
              s: { color: "blue" },
              t: "s",
              v: `${item.image}`,
              l: { Target: `${item.image}`, Tooltip: "Download Image" },
            },
            comments:
              item.comments.length > 0
                ? JSON.stringify(
                    item.comments.map((item) => {
                      return {
                        name: item.name,
                        comment: item.comment,
                        date: item.date,
                        time: item.time,
                      };
                    })
                  )
                : "No Comments ",
            AlertsAssignedTo:
              item.alertsAssignedMails?.length > 0
                ? JSON.stringify(
                    item.alertsAssignedMails.map((item) => {
                      return item;
                    })
                  )
                : "UnAssigned",
          };
        });
        const ws = XLSX.utils.json_to_sheet(userSheetData);
        const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
        const excelBuffer = XLSX.write(wb, {
          bookType: "xlsx",
          type: "array",
        });
        const data = new Blob([excelBuffer], { type: fileType });
        FileSaver.saveAs(data, fileName + fileExtension);
      } else {
        toast.error("Something went wrong while exporting the data.");
      }
      setSpinning(false);
    };

    return (
      <div className="w-1/2">
        <button
          className="bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-150"
          onClick={(e) => exportToCSV(fileName)}
          icon={FileUploadIcon}
        >
          Export
        </button>
      </div>
    );
  };

  const [userName, setUserName] = useState("");
  useEffect(() => {
    let token = localStorage.getItem("token");
    var decoded = jwt_decode(token);
    let { encryptedData } = decoded;
    decryptRequest(encryptedData).then((res) => {
      setUserName(JSON.parse(res).data);
    });
  }, []);

  return (
    <div className="p-2 md:p-3 bg-gray-50 min-h-screen">
      {spinning && <FullScreenLoader />}
      <ImageModalPopup
        imageModalOpen={isImageModalOpen}
        handleCloseImageModal={handleCloseImageModalForPopup}
        Image={selectedImageForModal}
        imageName={selectedImageNameForModal}
      />

      {/* --- RENDER THE NEW MODAL --- */}
      <AlertStatusModal
        isOpen={isStatusModalOpen}
        onClose={handleCloseStatusModal}
        onUpdate={handleUpdateAlertStatus}
        alert={selectedAlertForModal}
        assignedToOptions={assignedToOptions}
        userName={userName}
      />

      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <div className="flex items-center border border-teal-500 text-teal-700 p-2 px-4 rounded-lg shadow-sm bg-white">
          <span className="text-lg font-semibold">Total</span>
          <span className="ml-3 text-lg font-bold">
            {pagination.total || 0}
          </span>
        </div>
        <div className="flex items-center gap-2 relative">
          <ExportToExcel
            apiData={dataSource}
            fileName={"Alerts Sheet"}
            className="border-2 border-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-600 hover:text-white transition-colors"
          />
          <button
            ref={filtersButtonRef}
            onClick={() => setShowFilters(!showFilters)}
            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-150 flex items-center"
          >
            Filters
            <ChevronDownIcon
              className={`w-5 h-5 ml-1 transform transition-transform ${
                showFilters ? "rotate-180" : ""
              }`}
            />
          </button>
          {showFilters && (
            <div ref={filtersDropdownRef}>
              <FiltersDropdownContent
                onClose={() => setShowFilters(false)}
                onApplyFilters={handleApplyFilters}
                initialFilters={currentFilters}
                locationOptions={locationOptions}
                cameraOptions={cameraOptions}
                alertOptions={alertOptions}
                assignedToOptions={assignedToOptions}
              />
            </div>
          )}
        </div>
      </div>

      <div className="bg-white shadow-xl rounded-lg overflow-x-auto">
        <div className="min-w-[1000px] grid grid-cols-[auto_1fr_1fr_2fr_2fr_1.5fr_1fr_1fr] items-center p-3 border-b border-gray-200 bg-gray-50 text-sm font-semibold text-gray-600">
          <div className="px-2 py-1 flex items-center">
            {/* <input
              type="checkbox"
              className="form-checkbox h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              checked={isAllSelected}
              onChange={handleSelectAllChange}
              disabled={alerts.length === 0 || loading}
            /> */}
          </div>
          {/* ... Table Headers ... */}
          <div className="px-2 py-1 flex items-center">
            <CalendarDaysIcon className="w-5 h-5 mr-1 text-gray-500" /> Date
          </div>
          <div className="px-2 py-1 flex items-center">
            <ClockIcon className="w-5 h-5 mr-1 text-gray-500" /> Time
          </div>
          <div className="px-2 py-1 flex items-center">
            <ExclamationTriangleIcon className="w-5 h-5 mr-1 text-gray-500" />{" "}
            Alert
          </div>
          <div className="px-2 py-1 flex items-center">
            <CameraIcon className="w-5 h-5 mr-1 text-gray-500" /> Camera
          </div>
          <div className="px-2 py-1 flex items-center">
            <PhotoIcon className="w-5 h-5 mr-1 text-gray-500" /> Image
          </div>
          <div className="px-2 py-1 flex items-center">
            <MapPinIcon className="w-5 h-5 mr-1 text-gray-500" /> Location
          </div>
          <div className="px-2 py-1 flex items-center">
            <ClipboardDocumentListIcon className="w-5 h-5 mr-1 text-gray-500" />{" "}
            Status
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Spin size="large" />
          </div>
        ) : alerts.length > 0 ? (
          <div>
            {alerts.map((alert) => (
              <div
                key={alert._id}
                className={`min-w-[1000px] grid grid-cols-[auto_1fr_1fr_2fr_2fr_1.5fr_1fr_1fr] items-center p-3 border-b border-gray-200 hover:bg-gray-50 transition-colors duration-150 text-sm text-gray-700 ${
                  selectedAlertIds.has(alert._id) ? "bg-teal-50" : ""
                }`}
              >
                <div className="px-2 py-1">
                  {/* <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    checked={selectedAlertIds.has(alert._id)}
                    onChange={(event) =>
                      handleRowSelectChange(alert._id, event)
                    }
                  /> */}
                </div>
                <div className="px-2 py-1">{alert.date}</div>
                <div className="px-2 py-1">{alert.time}</div>
                <div className="px-2 py-1">{alert.alert}</div>
                <div className="px-2 py-1">{alert.camera}</div>
                <div className="px-2 py-1">
                  <img
                    src={alert.image}
                    alt={alert.alert}
                    style={{objectFit:"contain"}}
                    className="w-40 h-30 object-cover rounded-md border border-gray-300 cursor-pointer hover:opacity-80"
                    onClick={() => handleImageClickForPopup(alert.image, alert)}
                    onError={(e) =>
                      (e.target.src =
                        "https://via.placeholder.com/150?text=No+Image")
                    }
                  />
                </div>
                <div className="px-2 py-1">
                  <StatusTag
                    type="location"
                    text={alert.locationType || alert.location}
                  />
                </div>
                <div className="px-2 py-1">
                  <StatusTag
                    type="status"
                    text={alert.status}
                    onClick={() => handleOpenStatusModal(alert)}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex justify-center items-center h-64">
            <Empty description="No alerts found" />
          </div>
        )}
      </div>
      {!loading && pagination.total > 0 && (
        <div className="flex justify-center mt-6">
          <AntPagination
            current={pagination.currentPage}
            pageSize={pagination.limit}
            total={pagination.total}
            onChange={handlePaginationChange}
            showSizeChanger
            pageSizeOptions={["10", "20", "50", "100"]}
          />
        </div>
      )}
    </div>
  );
}

export default NewAlertsPage;
